import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { StripeCheckout } from '../components/StripeCheckout';
import { PLAN_TYPES, BILLING_PERIODS, type PlanType, type BillingPeriod } from '../services/stripeService';
import { CURRENT_APP_PRICING } from '../services/stripeService';

const StripePaywallPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState<PlanType>(PLAN_TYPES.STANDARD);
  const [selectedPeriod, setSelectedPeriod] = useState<BillingPeriod>(BILLING_PERIODS.MONTHLY);
  const [showCheckout, setShowCheckout] = useState(false);

  // 사용자 인증 확인
  useEffect(() => {
    if (!user) {
      alert(
        t('paywall.loginRequired', '로그인이 필요합니다.')
      );
    }
  }, [user, t]);

  // 플랜 선택 처리
  const handlePlanSelect = (plan: PlanType) => {
    setSelectedPlan(plan);
  };

  // 결제 기간 선택 처리
  const handlePeriodSelect = (period: BillingPeriod) => {
    setSelectedPeriod(period);
  };

  // 구독 시작
  const handleStartSubscription = () => {
    if (!user) {
      alert(
        t('paywall.loginRequired', '로그인이 필요합니다.')
      );
      return;
    }
    setShowCheckout(true);
  };

  // 체크아웃 성공 처리
  const handleCheckoutSuccess = () => {
    setShowCheckout(false);
    alert(
      t('paywall.subscriptionStarted', '구독이 시작되었습니다!')
    );
  };

  // 체크아웃 취소 처리
  const handleCheckoutCancel = () => {
    setShowCheckout(false);
  };

  // 가격 표시 (USD)
  const getPriceDisplay = (plan: PlanType, period: BillingPeriod) => {
    const amount = CURRENT_APP_PRICING[plan.toUpperCase() as keyof typeof CURRENT_APP_PRICING][period];
    const dollars = amount / 100;
    return `US$${dollars.toFixed(2)}`;
  };

  // 플랜별 기능 표시
  const getPlanFeatures = (plan: PlanType) => {
    switch (plan) {
      case PLAN_TYPES.CARD_ONLY:
        return [
          { feature: '2개 명함', included: true },
          { feature: '무제한 QR 생성', included: true },
          { feature: 'QR 로고 추가', included: true },
          { feature: '인보이스 지원', included: false },
          { feature: '통계 지원', included: false },
        ];
      case PLAN_TYPES.STANDARD:
        return [
          { feature: '5개 명함', included: true },
          { feature: '월 8개 인보이스', included: true },
          { feature: '무제한 QR 생성', included: true },
          { feature: 'QR 로고 추가', included: true },
          { feature: '명함별 특별 설정', included: true },
          { feature: '기본 통계', included: true },
        ];
      case PLAN_TYPES.PRO:
        return [
          { feature: '무제한 명함', included: true },
          { feature: '무제한 인보이스', included: true },
          { feature: '커스텀 도메인', included: true },
          { feature: '무제한 QR 생성', included: true },
          { feature: '고급 분석', included: true },
          { feature: '우선 지원', included: true },
        ];
      default:
        return [];
    }
  };

  // 플랜 이름 표시
  const getPlanName = (plan: PlanType) => {
    switch (plan) {
      case PLAN_TYPES.CARD_ONLY:
        return '기본';
      case PLAN_TYPES.STANDARD:
        return '스탠다드';
      case PLAN_TYPES.PRO:
        return '프로';
      default:
        return plan;
    }
  };

  if (!user) {
    return (
      <div className="auth-required">
        <h2>{t('paywall.loginRequired', '로그인이 필요합니다')}</h2>
        <p>{t('paywall.pleaseLogin', '구독을 시작하려면 먼저 로그인해주세요.')}</p>
      </div>
    );
  }

  return (
    <>
      {/* 헤더 */}
      <header className="paywall-header">
        <div className="header-content">
          <h1>ScanVCF</h1>
          <p>프리미엄 기능을 경험해보세요</p>
        </div>
      </header>

      <div className="stripe-paywall-page">
        <div className="header">
          <h1>{t('paywall.title', '구독 플랜 선택')}</h1>
          <p>{t('paywall.subtitle', '원하는 플랜을 선택하고 구독을 시작하세요')}</p>
        </div>

        {/* 결제 기간 선택 */}
        <div className="billing-period-selector">
          <h3>결제 기간 선택</h3>
          <div className="period-options">
            <button
              className={`period-btn ${selectedPeriod === BILLING_PERIODS.MONTHLY ? 'active' : ''}`}
              onClick={() => handlePeriodSelect(BILLING_PERIODS.MONTHLY)}
            >
              월간
            </button>
            <button
              className={`period-btn ${selectedPeriod === BILLING_PERIODS.YEARLY ? 'active' : ''}`}
              onClick={() => handlePeriodSelect(BILLING_PERIODS.YEARLY)}
            >
              연간
              <span className="discount-badge">20% 할인</span>
            </button>
          </div>
        </div>

        {/* 플랜 선택 */}
        <div className="plans-container">
          <div className="plan-grid">
            {/* Card Only Plan */}
            <div className={`plan-card ${selectedPlan === PLAN_TYPES.CARD_ONLY ? 'selected' : ''}`}>
              <div className="plan-header">
                <h3>{getPlanName(PLAN_TYPES.CARD_ONLY)}</h3>
                <div className="plan-price">
                  {getPriceDisplay(PLAN_TYPES.CARD_ONLY, selectedPeriod)}
                  <span className="period-text">
                    {selectedPeriod === BILLING_PERIODS.MONTHLY ? '/월' : '/년'}
                  </span>
                </div>
              </div>
              <div className="plan-features">
                {getPlanFeatures(PLAN_TYPES.CARD_ONLY).map((feature, index) => (
                  <div key={index} className={`feature ${feature.included ? 'included' : 'excluded'}`}>
                    {feature.included ? '✓' : '✗'} {feature.feature}
                  </div>
                ))}
              </div>
              <button
                className="select-plan-btn"
                onClick={() => handlePlanSelect(PLAN_TYPES.CARD_ONLY)}
              >
                {selectedPlan === PLAN_TYPES.CARD_ONLY ? '선택됨' : '구독하기'}
              </button>
            </div>

            {/* Standard Plan */}
            <div className={`plan-card ${selectedPlan === PLAN_TYPES.STANDARD ? 'selected' : ''}`}>
              <div className="plan-header">
                <h3>{getPlanName(PLAN_TYPES.STANDARD)}</h3>
                <div className="plan-price">
                  {getPriceDisplay(PLAN_TYPES.STANDARD, selectedPeriod)}
                  <span className="period-text">
                    {selectedPeriod === BILLING_PERIODS.MONTHLY ? '/월' : '/년'}
                  </span>
                </div>
              </div>
              <div className="plan-features">
                {getPlanFeatures(PLAN_TYPES.STANDARD).map((feature, index) => (
                  <div key={index} className={`feature ${feature.included ? 'included' : 'excluded'}`}>
                    {feature.included ? '✓' : '✗'} {feature.feature}
                  </div>
                ))}
              </div>
              <button
                className="select-plan-btn"
                onClick={() => handlePlanSelect(PLAN_TYPES.STANDARD)}
              >
                {selectedPlan === PLAN_TYPES.STANDARD ? '선택됨' : '구독하기'}
              </button>
            </div>

            {/* Pro Plan */}
            <div className={`plan-card ${selectedPlan === PLAN_TYPES.PRO ? 'selected' : ''}`}>
              <div className="plan-header">
                <h3>{getPlanName(PLAN_TYPES.PRO)}</h3>
                <div className="plan-price">
                  {getPriceDisplay(PLAN_TYPES.PRO, selectedPeriod)}
                  <span className="period-text">
                    {selectedPeriod === BILLING_PERIODS.MONTHLY ? '/월' : '/년'}
                  </span>
                </div>
              </div>
              <div className="plan-features">
                {getPlanFeatures(PLAN_TYPES.PRO).map((feature, index) => (
                  <div key={index} className={`feature ${feature.included ? 'included' : 'excluded'}`}>
                    {feature.included ? '✓' : '✗'} {feature.feature}
                  </div>
                ))}
              </div>
              <button
                className="select-plan-btn"
                onClick={() => handlePlanSelect(PLAN_TYPES.PRO)}
              >
                {selectedPlan === PLAN_TYPES.PRO ? '선택됨' : '구독하기'}
              </button>
            </div>
          </div>
        </div>

        {/* 구독 시작 버튼 */}
        <div className="subscription-actions">
          <button
            className="start-subscription-btn"
            onClick={handleStartSubscription}
            disabled={!selectedPlan || !selectedPeriod}
          >
            구독 시작하기
          </button>
        </div>

        {/* 체크아웃 컴포넌트 */}
        {showCheckout && (
          <div className="checkout-modal">
            <div className="checkout-content">
              <h3>결제 진행</h3>
              <StripeCheckout
                planType={selectedPlan}
                billingPeriod={selectedPeriod}
                userId={user.uid}
                onSuccess={handleCheckoutSuccess}
                onCancel={handleCheckoutCancel}
              />
            </div>
          </div>
        )}
      </div>

      {/* 푸터 */}
      <footer className="paywall-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>고객 지원</h4>
            <p>support@scanvcf.com</p>
          </div>
          <div className="footer-section">
            <h4>법적 고지</h4>
            <p>이용약관 | 개인정보처리방침</p>
          </div>
          <div className="footer-section">
            <h4>© 2024 ScanVCF</h4>
            <p>모든 권리 보유</p>
          </div>
        </div>
      </footer>

      <style jsx>{`
        /* 헤더 스타일 */
        .paywall-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 40px 20px;
          text-align: center;
        }

        .header-content h1 {
          font-size: 2.5rem;
          margin-bottom: 10px;
          font-weight: 700;
        }

        .header-content p {
          font-size: 1.2rem;
          opacity: 0.9;
        }

        /* 메인 콘텐츠 */
        .stripe-paywall-page {
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
          background: #f8f9fa;
          min-height: 100vh;
        }

        .header {
          text-align: center;
          margin-bottom: 40px;
          padding: 40px 0;
        }

        .header h1 {
          font-size: 2.5rem;
          color: #2c3e50;
          margin-bottom: 16px;
        }

        .header p {
          font-size: 1.2rem;
          color: #7f8c8d;
        }

        .billing-period-selector {
          text-align: center;
          margin-bottom: 40px;
        }

        .billing-period-selector h3 {
          margin-bottom: 20px;
          color: #2c3e50;
        }

        .period-options {
          display: flex;
          gap: 16px;
          justify-content: center;
        }

        .period-btn {
          padding: 12px 24px;
          border: 2px solid #e5e7eb;
          background: white;
          border-radius: 8px;
          cursor: pointer;
          font-size: 16px;
          font-weight: 600;
          transition: all 0.2s;
          position: relative;
        }

        .period-btn.active {
          border-color: #059669;
          background: #059669;
          color: white;
        }

        .discount-badge {
          position: absolute;
          top: -8px;
          right: -8px;
          background: #ef4444;
          color: white;
          font-size: 12px;
          padding: 4px 8px;
          border-radius: 12px;
          font-weight: 600;
        }

        .plans-container {
          margin-bottom: 40px;
        }

        .plan-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 24px;
        }

        .plan-card {
          background: white;
          border-radius: 12px;
          padding: 24px;
          border: 2px solid #e5e7eb;
          transition: all 0.2s;
          position: relative;
        }

        .plan-card.selected {
          border-color: #059669;
          box-shadow: 0 4px 12px rgba(5, 150, 105, 0.2);
        }

        .plan-header {
          text-align: center;
          margin-bottom: 24px;
        }

        .plan-header h3 {
          font-size: 1.5rem;
          color: #2c3e50;
          margin-bottom: 16px;
        }

        .plan-price {
          font-size: 2rem;
          font-weight: 700;
          color: #059669;
        }

        .period-text {
          font-size: 1rem;
          color: #6b7280;
          font-weight: 400;
        }

        .plan-features {
          margin-bottom: 24px;
        }

        .feature {
          padding: 8px 0;
          color: #374151;
          font-size: 14px;
        }

        .feature.included {
          color: #059669;
        }

        .feature.excluded {
          color: #9ca3af;
        }

        .select-plan-btn {
          width: 100%;
          padding: 12px;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .plan-card:not(.selected) .select-plan-btn {
          background: #f3f4f6;
          color: #374151;
        }

        .plan-card:not(.selected) .select-plan-btn:hover {
          background: #e5e7eb;
        }

        .plan-card.selected .select-plan-btn {
          background: #059669;
          color: white;
        }

        .subscription-actions {
          text-align: center;
        }

        .start-subscription-btn {
          background: #059669;
          color: white;
          border: none;
          padding: 16px 32px;
          border-radius: 8px;
          font-size: 18px;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .start-subscription-btn:hover {
          background: #047857;
        }

        .checkout-modal {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .checkout-content {
          background: white;
          border-radius: 12px;
          padding: 24px;
          max-width: 500px;
          width: 90%;
          max-height: 90vh;
          overflow-y: auto;
        }

        .checkout-content h3 {
          margin-bottom: 20px;
          text-align: center;
          color: #2c3e50;
        }

        .auth-required {
          text-align: center;
          padding: 60px 20px;
          color: #6b7280;
        }

        .auth-required h2 {
          margin-bottom: 16px;
          color: #374151;
        }

        /* 푸터 스타일 */
        .paywall-footer {
          background: #1f2937;
          color: white;
          padding: 40px 20px;
          margin-top: 60px;
        }

        .footer-content {
          max-width: 1200px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 40px;
        }

        .footer-section h4 {
          color: #f9fafb;
          margin-bottom: 16px;
          font-size: 1.1rem;
        }

        .footer-section p {
          color: #d1d5db;
          line-height: 1.6;
        }

        @media (max-width: 768px) {
          .plan-grid {
            grid-template-columns: 1fr;
          }
          
          .period-options {
            flex-direction: column;
            align-items: center;
          }

          .footer-content {
            grid-template-columns: 1fr;
            text-align: center;
          }
        }
      `}</style>
    </>
  );
};

export default StripePaywallPage;
